import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const PLATFORMS = [
    { value: "whatsapp", label: "واتساب" },
    { value: "phone", label: "اتصال هاتفي" },
    { value: "instagram", label: "انستغرام" },
    { value: "snapchat", label: "سناب شات" },
    { value: "tiktok", label: "تيك توك" },
    { value: "twitter", label: "تويتر / X" },
    { value: "facebook", label: "فيسبوك" },
    { value: "youtube", label: "يوتيوب" },
    { value: "other", label: "منصة أخرى" },
];

const EMPTY_SOCIAL = { platform: "instagram", handle: "", url: "", enabled: true, order: 0 };

export const SocialsPanel = () => {
    const [socials, setSocials] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = useCallback(() => {
        api.get("/socials/all").then((r) => setSocials(r.data)).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [load]);

    const remove = async (s) => {
        if (!window.confirm(`حذف منصة «${s.platform}»؟`)) return;
        try {
            await api.delete(`/socials/${s.id}`);
            toast.success("تم الحذف");
            load();
        } catch {
            toast.error("تعذر الحذف");
        }
    };

    const inputCls =
        "bg-[#0A1228]/70 border border-white/10 rounded-xl py-2.5 px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300";

    return (
        <div data-testid="admin-socials-panel" className="max-w-3xl">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display font-bold text-xl text-white">حسابات التواصل الاجتماعي</h2>
                    <p className="text-sm text-slate-500 mt-1">تظهر الحسابات المفعّلة في الفوتر وقسم التواصل</p>
                </div>
                <button
                    onClick={() => setEditing({ ...EMPTY_SOCIAL, order: socials.length + 1 })}
                    data-testid="admin-add-social-button"
                    className="flex items-center gap-2 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-full px-5 py-2.5 hover:bg-amber-300 transition-colors duration-300"
                >
                    <Plus className="w-4 h-4" /> إضافة منصة
                </button>
            </div>

            {editing && (
                <SocialForm
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { load(); setEditing(null); }}
                    inputCls={inputCls}
                />
            )}

            <div className="space-y-2">
                {socials.map((s) => (
                    <div key={s.id} className="flex items-center justify-between border border-white/10 rounded-2xl px-5 py-3.5 bg-[#101B3D]/60" data-testid={`social-row-${s.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                            <span className="font-bold text-white text-sm">
                                {PLATFORMS.find((p) => p.value === s.platform)?.label || s.platform}
                            </span>
                            <span className="text-xs text-slate-500 truncate" dir="ltr">{s.handle || s.url}</span>
                            {!s.enabled && <span className="text-[10px] font-bold text-red-400">معطّل</span>}
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <button
                                onClick={() => setEditing({ ...s })}
                                data-testid={`social-edit-${s.id}`}
                                className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-amber-400 hover:border-amber-400/50 transition-colors duration-200"
                                aria-label="تعديل"
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => remove(s)}
                                data-testid={`social-delete-${s.id}`}
                                className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-500/50 transition-colors duration-200"
                                aria-label="حذف"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

function SocialForm({ initial, onClose, onSaved, inputCls }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = { ...form, order: Number(form.order) };
        delete payload.id;
        try {
            if (isEdit) {
                await api.put(`/socials/${initial.id}`, payload);
                toast.success("تم التحديث");
            } else {
                await api.post("/socials", payload);
                toast.success("تمت الإضافة");
            }
            onSaved();
        } catch {
            toast.error("تعذر الحفظ");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="spotlight-card border border-amber-400/30 rounded-2xl p-5 mb-6" data-testid="social-form">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-white">{isEdit ? "تعديل منصة" : "منصة جديدة"}</h3>
                <button type="button" onClick={onClose} className="text-slate-500 hover:text-white" aria-label="إغلاق">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <select value={form.platform} onChange={(e) => set("platform", e.target.value)} data-testid="social-platform" className={inputCls}>
                    {PLATFORMS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                </select>
                <input value={form.handle} onChange={(e) => set("handle", e.target.value)} placeholder="اسم الحساب / الرقم" data-testid="social-handle" className={inputCls} dir="ltr" />
                <input required value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="الرابط الكامل https://..." data-testid="social-url" className={`${inputCls} sm:col-span-2`} dir="ltr" />
                <input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} placeholder="الترتيب" data-testid="social-order" className={inputCls} dir="ltr" />
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="social-enabled" className="w-4 h-4 accent-amber-400" />
                    مفعّل (يظهر في الموقع)
                </label>
            </div>
            <button type="submit" disabled={saving} data-testid="social-form-submit" className="mt-4 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-xl px-6 py-2.5 hover:bg-amber-300 transition-colors duration-300 disabled:opacity-50">
                {saving ? "جاري الحفظ..." : isEdit ? "حفظ التعديلات" : "إضافة المنصة"}
            </button>
        </form>
    );
}
